// src/features/services/agent/components/CreateThreadPanel.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Create a new AI agent thread for SKU generation.
// Text + voice + optional images → AI processing → proposal.
// Real-time SSE streaming shows pipeline progress stages.
// PAC Glass surfaces. Data owned by useAgentThreads hook.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback, useRef } from 'react';
import { useAgentThreads, PIPELINE_STAGES, type StreamingState } from '../hooks/useAgent';
import type { PipelineStage } from '../api/agentApi';
import VoiceRecorder from '@/shared/components/VoiceRecorder';

const CATEGORIES = [
  { value: '',          label: 'Auto-detect' },
  { value: 'iot',       label: 'IoT' },
  { value: 'component', label: 'Component' },
  { value: 'sensor',    label: 'Sensor' },
  { value: 'assembly',  label: 'Assembly' },
  { value: 'retail',    label: 'Retail' },
  { value: 'general',   label: 'General' },
];

interface ImageAttachment {
  data: string;
  mime_type: string;
  filename: string;
}

interface CreateThreadPanelProps {
  onThreadCreated?: (threadKey: string) => void;
}

export default function CreateThreadPanel({ onThreadCreated }: CreateThreadPanelProps) {
  const { create, streaming, clearStream } = useAgentThreads();
  const [successKey, setSuccessKey] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [images, setImages] = useState<ImageAttachment[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [usedVoice, setUsedVoice] = useState(false);
  const [audioBase64, setAudioBase64] = useState<string | null>(null);

  const resetForm = useCallback((createdKey?: string) => {
    setDescription('');
    setCategory('');
    setImages([]);
    setUsedVoice(false);
    setAudioBase64(null);
    if (createdKey) {
      setSuccessKey(createdKey);
      setTimeout(() => {
        setSuccessKey(null);
        setShowForm(false);
      }, 2000);
    } else {
      setShowForm(false);
    }
  }, []);

  const processFiles = useCallback((fileList: FileList | File[]) => {
    const arr = Array.from(fileList as any).map(f => f as File).filter((f: File) => (f.type || '').startsWith('image/'));
    if (arr.length === 0) return;

    const remaining = 10 - images.length;
    const toRead = arr.slice(0, remaining);

    for (const file of toRead) {
      const f = file as File;
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setImages(prev => [...prev, {
          data: base64,
          mime_type: f.type || 'image/jpeg',
          filename: f.name,
        }]);
      };
      reader.readAsDataURL(f);
    }

    if (fileRef.current) fileRef.current.value = '';
  }, [images.length]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    processFiles(files);
  }, [processFiles]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    processFiles(dt.files);
  }, [processFiles]);

  const removeImage = useCallback((idx: number) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || description.trim().length < 10) return;

    create.mutate(
      {
        description: description.trim(),
        images: images.length > 0 ? images : undefined,
        context: category ? { category } : undefined,
        voice_input: usedVoice || undefined,
        audio_data: audioBase64
          ? { data: audioBase64, mime_type: 'audio/webm', filename: 'voice-capture.webm' }
          : undefined,
      },
      {
        onSuccess: (thread) => {
          // SSE stream starts automatically via the hook
          onThreadCreated?.(thread.thread_key);
          resetForm(thread.thread_key);
        },
      },
    );
  };

  const isProcessing = create.isPending || streaming.active;
  const showProgress = streaming.active || (streaming.stage === 'complete' && !successKey);

  return (
    <div className="pac-glass rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-neutral-200 uppercase tracking-wider">
          AI SKU Builder
        </h2>
        <button
          onClick={() => setShowForm(f => !f)}
          disabled={isProcessing}
          className="pac-glass-btn text-xs px-3 py-1.5 rounded border border-white/10 text-neutral-300
                     hover:border-purple-500/50 hover:text-purple-400 transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {showForm ? 'Cancel' : '+ New Thread'}
        </button>
      </div>

      {/* ── Streaming Progress Indicator ─────────────────────────────── */}
      {showProgress && streaming.threadKey && (
        <PipelineProgressBar streaming={streaming} />
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="pac-glass-raised rounded p-4 space-y-4 border border-neutral-800">
          {/* Description + Voice */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Describe the product(s) you want to create
            </label>
            <div className="relative">
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                placeholder="e.g. I have an SHT30 temperature sensor and an ESP32 microcontroller. Together they make a PACDaemon IoT Telemetry Device with a 3D-printed enclosure."
                className="pac-glass-input w-full rounded px-3 py-2 pr-14 text-sm text-neutral-200
                           bg-white/[0.04] border border-white/10 focus:border-purple-500/50
                           focus:ring-1 focus:ring-purple-500/30 outline-none resize-none"
                required
                minLength={10}
                maxLength={10000}
              />
              {/* Voice recorder — top-right of textarea */}
              <div className="absolute top-2 right-2">
                <VoiceRecorder
                  compact
                  onTranscript={(text, result) => {
                    setUsedVoice(true);
                    setDescription(prev => {
                      const separator = prev.trim().length > 0 ? ' ' : '';
                      return prev + separator + text;
                    });
                    // Capture raw audio blob for quarantine archival
                    if (result?.audioBlob) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        const b64 = (reader.result as string).split(',')[1];
                        if (b64) setAudioBase64(b64);
                      };
                      reader.readAsDataURL(result.audioBlob);
                    }
                  }}
                  disabled={create.isPending}
                />
              </div>
            </div>
            <p className="text-[10px] text-neutral-600 mt-1">
              {description.length}/10000 — Minimum 10 characters · Speak or type
            </p>
          </div>

          {/* Category hint */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Category Hint (optional)
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="pac-glass-input w-48 rounded px-3 py-2 text-sm text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-purple-500/50
                         focus:ring-1 focus:ring-purple-500/30 outline-none"
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Image attachments — enhanced drop zone */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Images (optional, max 10)
            </label>

            {/* Drop zone area */}
            <div
              className={`
                relative rounded-lg border-2 border-dashed transition-all duration-200
                ${isDragging
                  ? 'border-purple-500/60 bg-purple-500/[0.06] shadow-[0_0_20px_rgba(168,85,247,0.1)]'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                }
                ${images.length >= 10 ? 'opacity-40 pointer-events-none' : ''}
              `}
              onDragOver={onDragOver}
              onDragEnter={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <div className="flex flex-col items-center justify-center py-6 px-4">
                {/* Upload icon */}
                <svg
                  className={`w-8 h-8 mb-2 transition-colors ${isDragging ? 'text-purple-400' : 'text-neutral-600'}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p className={`text-xs mb-1 ${isDragging ? 'text-purple-300' : 'text-neutral-400'}`}>
                  {isDragging ? 'Drop images here' : 'Drag & drop images here'}
                </p>
                <p className="text-[10px] text-neutral-600 mb-3">or</p>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={images.length >= 10}
                  className="pac-glass-btn text-xs px-3 py-1.5 rounded border border-white/10
                             text-neutral-300 hover:border-purple-500/50 transition-colors
                             disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Browse Files
                </button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Image thumbnails */}
            {images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {images.map((img, i) => (
                  <div
                    key={i}
                    className="pac-glass-subtle rounded-md overflow-hidden border border-white/5
                               flex items-center gap-2 pr-2 max-w-[200px]"
                  >
                    <img
                      src={`data:${img.mime_type};base64,${img.data}`}
                      alt={img.filename}
                      className="w-8 h-8 object-cover flex-shrink-0"
                    />
                    <span className="text-[10px] text-neutral-400 truncate">{img.filename}</span>
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="text-rose-400 hover:text-rose-300 flex-shrink-0 ml-auto"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <span className="text-[10px] text-neutral-600 self-center ml-1">
                  {images.length}/10
                </span>
              </div>
            )}
          </div>

          {/* Error display */}
          {create.isError && (
            <p className="text-xs text-rose-400">{create.error.message}</p>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isProcessing || description.trim().length < 10}
              className="pac-glass-btn px-4 py-2 rounded text-sm border border-purple-500/40
                         text-purple-400 hover:border-purple-400 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Processing…' : 'Generate Proposal'}
            </button>
          </div>

          {/* Legacy pending indicator (mutation in-flight before SSE connects) */}
          {create.isPending && !streaming.active && (
            <div className="pac-glass-subtle rounded p-3 border border-purple-900/30">
              <p className="text-xs text-purple-400 animate-pulse">
                Submitting request…
              </p>
            </div>
          )}

          {successKey && (
            <div className="pac-glass-subtle rounded p-3 border border-emerald-900/30">
              <p className="text-xs text-emerald-400">
                ✓ Thread <span className="font-mono">{successKey}</span> created — opening preview…
              </p>
            </div>
          )}
        </form>
      )}
    </div>
  );
}

// ── Pipeline Progress Bar ────────────────────────────────────────────────

function PipelineProgressBar({ streaming }: { streaming: StreamingState }) {
  const currentIdx = PIPELINE_STAGES.findIndex(s => s.key === streaming.stage);
  const isFailed = streaming.stage === 'failed';
  const isComplete = streaming.stage === 'complete';
  const lastMessage = streaming.events.length > 0
    ? streaming.events[streaming.events.length - 1].message
    : null;

  return (
    <div className="pac-glass-raised rounded-lg p-4 border border-purple-900/30 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
          Pipeline Progress
        </p>
        <div className="flex items-center gap-2">
          {streaming.traceId && (
            <span className="font-mono text-[9px] text-neutral-700" title={streaming.traceId}>
              trace:{streaming.traceId.slice(0, 8)}
            </span>
          )}
          <span className="font-mono text-[10px] text-purple-400">
            {streaming.threadKey}
          </span>
        </div>
      </div>

      {/* Stage dots */}
      <div className="flex items-center gap-1">
        {PIPELINE_STAGES.map((s, i) => {
          const isPast = i < currentIdx;
          const isCurrent = i === currentIdx;
          const isFutureOrFailed = !isPast && !isCurrent;

          let dotCls = 'bg-neutral-800 border-neutral-700 text-neutral-600';
          if (isPast)    dotCls = 'bg-emerald-900/40 border-emerald-700/40 text-emerald-400';
          if (isCurrent && !isFailed) dotCls = 'bg-purple-900/40 border-purple-500/40 text-purple-400';
          if (isCurrent && isFailed)  dotCls = 'bg-rose-900/40 border-rose-700/40 text-rose-400';

          return (
            <React.Fragment key={s.key}>
              <div
                className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[10px]
                            transition-all duration-300 ${dotCls}
                            ${isCurrent && !isFailed && !isComplete ? 'animate-pulse' : ''}`}
              >
                <span>{s.icon}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < PIPELINE_STAGES.length - 1 && (
                <div className={`flex-1 h-px ${isPast ? 'bg-emerald-700/40' : 'bg-neutral-800'} transition-colors duration-300`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Status message */}
      {lastMessage && (
        <p className={`text-xs ${isFailed ? 'text-rose-400' : isComplete ? 'text-emerald-400' : 'text-purple-400'}`}>
          {lastMessage}
        </p>
      )}
      {isFailed && streaming.error && (
        <p className="text-xs text-rose-400">{streaming.error}</p>
      )}
    </div>
  );
}
